/**
 * Format a trading symbol for natural speech pronunciation.
 * - Crypto perpetuals (ETHUSD, BTCUSD): spell letter by letter
 * - NSE options (NIFTY26APR25000CE): say "NIFTY April 25000 Call"
 * - NSE futures (NIFTY26APRFUT): say "NIFTY April Futures"
 * - Short symbols (≤4 chars): spell letter by letter
 * - Everything else (NSE equities): say as a word
 */
export function formatSymbolForSpeech(symbol: string): string {
  const clean = symbol.replace(/\.(P|PERP|F)$/i, '').trim().toUpperCase();

  // Crypto perpetuals: ends with USD or USDT
  if (/^[A-Z]+(USDT?|BTC|ETH)$/.test(clean)) {
    return clean.split('').join(' ');
  }

  // NSE Options: e.g. NIFTY26APR25000CE, BANKNIFTY26MAY55000PE
  const optMatch = clean.match(/^([A-Z]+)\d{2}([A-Z]{3})\d*(\d{4,})(CE|PE)$/i);
  if (optMatch) {
    const base = optMatch[1];
    const mon = optMatch[2];
    const strike = parseInt(optMatch[3]);
    const type = optMatch[4].toUpperCase() === 'CE' ? 'Call' : 'Put';
    const baseSpoken = base.length <= 4 ? base.split('').join(' ') : base;
    return `${baseSpoken} ${mon} ${strike.toLocaleString()} ${type}`;
  }

  // NSE Futures: e.g. NIFTY26APRFUT
  const futMatch = clean.match(/^([A-Z]+)\d{2}([A-Z]{3})FUT$/i);
  if (futMatch) {
    const base = futMatch[1].length <= 4 ? futMatch[1].split('').join(' ') : futMatch[1];
    return `${base} ${futMatch[2]} Futures`;
  }

  // Short symbols: spell out
  if (clean.length <= 4) return clean.split('').join(' ');

  // Default: say as a word (NSE equities like RELIANCE, INFY)
  return clean;
}

/**
 * Build a very short speech text for an alert.
 * e.g. "Buy E T H U S D" or "Sell RELIANCE"
 */
export function buildSpeechText(action: string, symbol: string): string {
  if (!symbol) return '';
  const symbolSpoken = formatSymbolForSpeech(symbol);
  const actionWords: Record<string, string> = {
    BUY: 'Buy', SELL: 'Sell', SHORT: 'Short', COVER: 'Cover', NOTIFY: 'Alert on'
  };
  const actionWord = actionWords[action?.toUpperCase()] || (action || 'Alert on');
  return `${actionWord} ${symbolSpoken}`;
}

/**
 * Speech Alert Service - Uses Web Speech API to announce trading alerts
 */
class SpeechAlertService {
  private queue: Array<{ text: string; toneType: 'buy' | 'sell' | 'error' | 'alert' }> = [];
  private speaking = false;
  /**
   * Dedup map for the de-facto identity of a single user-visible event.
   * Backend can emit the same alert via multiple paths (TradingView webhook,
   * internal strategy alert, renotify trigger, periodic re-evaluation) and
   * each path arrives as a separate `alert:new` socket event. Without dedup,
   * a single event was being spoken 2-3 times in quick succession.
   *
   * Key: `${alertId}` (or `${text}` when no id is available).
   * Value: timestamp (ms) the key was last accepted.
   */
  private spokenRecently: Map<string, number> = new Map();
  private readonly DEDUP_WINDOW_MS = 30_000;

  isSupported(): boolean {
    return 'speechSynthesis' in window;
  }

  /**
   * Queue a speech utterance.
   * @param text  The text to speak.
   * @param toneType  Audio tone played before the utterance.
   * @param dedupKey  Optional key (typically the alert id). If supplied and the
   *   same key was spoken within DEDUP_WINDOW_MS, this call is a no-op. Falls
   *   back to the text itself when no key is provided.
   */
  speak(text: string, toneType: 'buy' | 'sell' | 'error' | 'alert' = 'alert', dedupKey?: string): void {
    if (!this.isSupported()) return;
    if (!text) return;
    const key = dedupKey || text;
    const now = Date.now();
    const last = this.spokenRecently.get(key);
    if (last != null && now - last < this.DEDUP_WINDOW_MS) {
      // Suppressed duplicate — same event arriving via a second emit path.
      return;
    }
    this.spokenRecently.set(key, now);
    // Opportunistic cleanup so the map doesn't grow unbounded
    if (this.spokenRecently.size > 200) {
      const cutoff = now - 5 * 60_000;
      this.spokenRecently.forEach((ts, k) => {
        if (ts < cutoff) this.spokenRecently.delete(k);
      });
    }
    this.queue.push({ text, toneType });
    if (!this.speaking) {
      this.processQueue();
    }
  }

  stop(): void {
    this.queue = [];
    this.speaking = false;
    if (this.isSupported()) {
      window.speechSynthesis.cancel();
    }
  }

  private async playTone(type: 'buy' | 'sell' | 'error' | 'alert' = 'alert'): Promise<void> {
    return new Promise((resolve) => {
      try {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) { resolve(); return; }
        const ctx = new AudioCtx();
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);
        const tones = {
          buy:   { freq1: 440, freq2: 660, wave: 'sine'     as OscillatorType, dur: 0.3 },
          sell:  { freq1: 660, freq2: 440, wave: 'sine'     as OscillatorType, dur: 0.3 },
          error: { freq1: 220, freq2: 220, wave: 'sawtooth' as OscillatorType, dur: 0.4 },
          alert: { freq1: 800, freq2: 800, wave: 'sine'     as OscillatorType, dur: 0.2 },
        };
        const t = tones[type] || tones.alert;
        oscillator.type = t.wave;
        oscillator.frequency.setValueAtTime(t.freq1, ctx.currentTime);
        oscillator.frequency.linearRampToValueAtTime(t.freq2, ctx.currentTime + t.dur);
        gainNode.gain.setValueAtTime(0.25, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t.dur);
        oscillator.start();
        oscillator.stop(ctx.currentTime + t.dur);
        oscillator.onended = () => { ctx.close(); resolve(); };
      } catch { resolve(); }
    });
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      this.speaking = false;
      return;
    }

    this.speaking = true;
    const item = this.queue.shift()!;

    await this.playTone(item.toneType);

    const utterance = new SpeechSynthesisUtterance(item.text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    // Chrome/Safari quirks: utterance.onend occasionally never fires (browser
    // suspends the synthesis queue when the tab loses focus, when the engine
    // hits a hidden error, or after ~15s of idle time). Without a fallback the
    // service stays "speaking" forever and no further alert is announced.
    let advanced = false;
    const advance = () => {
      if (advanced) return;
      advanced = true;
      // small delay so a queued onend after our timeout doesn't double-advance
      setTimeout(() => this.processQueue(), 50);
    };
    utterance.onend = advance;
    utterance.onerror = advance;

    // Estimate spoken duration: ~150ms per character + 1s buffer. Cap at 15s.
    const fallbackMs = Math.min(15000, Math.max(2000, item.text.length * 150 + 1000));
    setTimeout(advance, fallbackMs);

    // Some Chrome versions silently pause the queue if speak() is called while
    // the engine is in a paused state — kick it.
    try { window.speechSynthesis.resume(); } catch { /* ignore */ }
    window.speechSynthesis.speak(utterance);
  }
}

export const speechService = new SpeechAlertService();
